export const DISCLOSURE =
  "This case study describes prior professional experience of Improve Systems' principal. Organization and product identifiers have been omitted to protect confidentiality.";

export const CASE_STUDIES = [
  {
    slug: "high-volume-case-reduction",
    index: "01",
    tag: "ROOT-CAUSE ANALYSIS · VOLUME REDUCTION",
    title: "Reducing High-Volume Customer Cases Through Root-Cause Analysis",
    context:
      "A global enterprise software company serving professional creative markets — roughly 7,000 customer cases per month across a multi-product portfolio.",
    summary:
      "High case volume was being treated as a staffing problem. Structured classification showed it was a systems problem — and a small number of recurring causes accounted for a disproportionate share of the work.",
    lifecycle: [
      {
        label: "Problem",
        text: "Roughly 7,000 customer cases arrived each month, queues grew, and responses were anecdote-driven — whichever issue was loudest that week got attention. There was no reliable, organization-wide view of why customers were contacting support at scale.",
      },
      {
        label: "Discovery",
        text: "Developed a case-categorization taxonomy and applied it across a large, representative sample — reviewing what customers were actually asking, which product or process was involved, and what condition had produced the contact. Intake categories proved unreliable: visible symptoms were routinely mislabeled at first contact.",
      },
      {
        label: "Evidence",
        text: "A minority of recurring causes produced a majority of the volume. Far from a diffuse spread of one-off issues, most of the total concentrated in a handful of themes tied to specific product behaviors, policies, and process gaps.",
      },
      {
        label: "Root cause",
        text: "The drivers were structural: specific product UX friction points, entitlement and licensing flows that failed silently, documentation gaps, and internal process handoffs — each generating the same cases, repeatedly, month after month.",
      },
      {
        label: "Requirements",
        text: "Built a prioritized improvement backlog scoring each cause by impact (case volume, customer severity) and effort (engineering, policy, content, process). Established the baseline up front, and the specific measure each intervention would be held to.",
      },
      {
        label: "Intervention",
        text: "A portfolio of matched interventions rather than one large program: product UX changes, corrections to entitlement flows, targeted documentation and deflection content, workflow changes inside support, and automation of repetitive resolutions.",
      },
      {
        label: "Outcome",
        text: "Monthly case volume was reduced by roughly half. Just as durably, the categorization methodology became the standing instrument the organization uses to decide where improvement effort goes.",
      },
    ],
    demonstrates:
      "The Discover and Define stages in practice: entering a complicated environment, making its workload legible, and prioritizing intervention by evidence rather than volume of complaint.",
  },
  {
    slug: "upstream-integration-failure",
    index: "02",
    tag: "SYSTEMS TRACING · INTEGRATION FAILURE",
    title: "Tracing a Customer-Facing Failure to an Upstream Data Integration",
    context:
      "A global enterprise software company serving professional creative markets — customer-facing commerce, entitlement, and licensing systems serving a global customer base.",
    summary:
      "Customers were hitting what looked like a licensing problem at the moment of purchase and activation. It wasn't. The visible failure sat at the very end of a transaction and data chain — the origin was a silent integration failure upstream.",
    lifecycle: [
      {
        label: "Problem",
        text: "Customers who had purchased products were unable to use them. Cases presented as licensing errors, and frontline teams treated them as licensing issues — because that is where the error surfaced.",
      },
      {
        label: "Discovery",
        text: "Traced individual failing cases end-to-end through the full chain: commerce transaction, entitlement creation, licensing services, and the downstream product systems and data feeds. The object of the exercise was to find where the record stopped being correct.",
      },
      {
        label: "Evidence",
        text: "Records were accurate at the point of purchase and entitlement. Downstream, the same records were stale. A data integration between systems had failed silently — producing no errors at the source, only wrongness at the destination. No alert had fired.",
      },
      {
        label: "Root cause",
        text: "An integration failure causing stale downstream data. The “licensing problem” was simply the last place the failure became visible — not the place it originated.",
      },
      {
        label: "Requirements",
        text: "Defined what needed to change: repair the failing integration, add monitoring and alerting on data freshness across the chain, and establish reconciliation between source and downstream systems so drift is detected before customers find it.",
      },
      {
        label: "Intervention",
        text: "Integration repair; freshness monitoring with alerting; a reconciliation step comparing entitled state to delivered state; and updated support diagnostic paths so this symptom class routes to its cause faster.",
      },
      {
        label: "Outcome",
        text: "The recurring symptom class was eliminated at the source, and the organization moved from discovering this failure mode through customer complaints to detecting it directly. It remains a canonical example of a principle the firm is built on: the point where an error becomes visible is not necessarily where it originated.",
      },
    ],
    demonstrates:
      "Cross-system tracing: following an outcome backward through integrations and data — past the team that owns the symptom — until the originating condition is found.",
  },
  {
    slug: "ai-assisted-continuous-monitoring",
    index: "03",
    tag: "OPERATIONAL INTELLIGENCE · AI APPLIED SELECTIVELY",
    title: "Moving from Retrospective Analysis to AI-Assisted Continuous Monitoring",
    context:
      "A global enterprise software company serving professional creative markets — evolving a proven manual analysis practice into a continuous, instrumented capability.",
    summary:
      "Manual, periodic case review could only ever describe the past. The work evolved into AI-assisted classification at scale, dashboards, pattern identification, and earlier anomaly detection — with people validating the model, not replaced by it.",
    lifecycle: [
      {
        label: "Problem",
        text: "The root-cause methodology worked, but it was manual and retrospective: samples reviewed by hand, findings weeks old by the time they were compiled, and emerging issues invisible until they were already large.",
      },
      {
        label: "Discovery",
        text: "Assessed where machine classification could genuinely help: high-volume, repeatable categorization against the existing taxonomy — a real pattern-recognition problem at a scale humans could not sustain. Just as important, defined what the model must never do alone: set priorities, or close the loop without human review.",
      },
      {
        label: "Evidence",
        text: "Validated AI-assisted classification against human categorization before scaling it. On well-defined categories, agreement was strong enough to extend coverage from samples to the full case stream; ambiguous cases were routed to human review by design.",
      },
      {
        label: "The constraint",
        text: "The limiting condition was never willingness to act — it was the cost of understanding. Classification throughput, not analytical quality, was the bottleneck the intervention had to remove.",
      },
      {
        label: "Requirements",
        text: "A structured classification pipeline; confidence thresholds with human-in-the-loop validation; dashboards tracking category volumes over time; anomaly detection on emerging patterns; and explicit governance and controls for how the model is used.",
      },
      {
        label: "Intervention",
        text: "AI-assisted classification at full volume, standing dashboards, pattern identification, and alerting on category anomalies — turning a periodic report into continuous operational intelligence.",
      },
      {
        label: "Outcome",
        text: "Detection moved from weeks to near-real-time, and classification scaled from samples to the full stream. The organization now sees emerging issues while they are still small. This is the operating pattern directly relevant to program-integrity work such as SNAP: structured signals, classification at scale, and human judgment reserved for where it matters.",
      },
    ],
    demonstrates:
      "The Measure stage — and how AI earns its place inside an advisory practice: validated against a defined need, deployed with controls, accountable to an outcome. Never as a headline.",
  },
];

export const IN_PREPARATION = [
  {
    title: "Enterprise Licensing Transformation — Product Discovery",
    note: "Discovery across multiple products and customer types: documenting use cases and requirements, mapping dependencies, and determining where a proposed common platform could — and could not — satisfy real customer requirements.",
  },
  {
    title: "Complex Enterprise Identity & Entitlement Architecture",
    note: "Reframing organizational hierarchy from a vendor-maintained model toward a customer-led identity model, with the customer's IdP and groups as the living organizational structure and the platform providing entitlement boundaries and policy.",
  },
];
